import { useState } from "react";

interface Props {
  value: number | null;
  onChange: (n: number) => void;
  ariaLabel: string;
  lowLabel?: string;
  highLabel?: string;
}

const STARS = [1, 2, 3, 4, 5] as const;

export function StarRating({ value, onChange, ariaLabel, lowLabel, highLabel }: Props) {
  const [hover, setHover] = useState<number | null>(null);
  const showAs = hover ?? value ?? 0;

  return (
    <div>
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        className="flex justify-between gap-1"
        onMouseLeave={() => setHover(null)}
      >
        {STARS.map((n) => {
          const filled = n <= showAs;
          const checked = value === n;
          return (
            <button
              key={n}
              type="button"
              // biome-ignore lint/a11y/useSemanticElements: custom-styled radiogroup; <input type="radio"> doesn't support the visual treatment
              role="radio"
              aria-checked={checked}
              aria-label={`${n} star${n === 1 ? "" : "s"}`}
              onClick={() => onChange(n)}
              onMouseEnter={() => setHover(n)}
              onFocus={() => setHover(n)}
              onBlur={() => setHover(null)}
              className="flex min-w-0 flex-1 select-none items-center justify-center rounded-xl border border-transparent py-4 transition-colors active:scale-95 focus:outline-none focus-visible:border-amber/60"
            >
              <Star filled={filled} />
            </button>
          );
        })}
      </div>
      {(lowLabel || highLabel) && (
        <div className="mt-2 flex justify-between text-xs uppercase tracking-wider text-cream-dim">
          <span>{lowLabel}</span>
          <span>{highLabel}</span>
        </div>
      )}
    </div>
  );
}

function Star({ filled }: { filled: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={`h-12 w-12 transition-colors sm:h-14 sm:w-14 ${filled ? "text-amber" : "text-cream/20"}`}
      fill="currentColor"
    >
      <path d="M12 2.5l2.95 6.36 6.8.78-5.05 4.7 1.43 6.86L12 17.77l-6.13 3.43 1.43-6.86L2.25 9.64l6.8-.78L12 2.5z" />
    </svg>
  );
}
